class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.requestId = 0;
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    const onInput = FoxTheme.utils.debounce((requestId) => {
      if (requestId === this.requestId) this.onChange();
    }, 300);
    this.input.addEventListener('input', () => {
      // Invalidate immediately, before the next debounced request is started.
      this.invalidatePendingRequest();
      onInput(this.requestId);
    });
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('keydown', this.onKeyDown.bind(this));
    this.addEventListener('focusout', event => {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) this.toggleSearchState(false);
    });
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => this.toggleSearchState(false));

    this.updateScopeLabel();
    const encodedQuery = this.querySelector('[data-search-query]');
    if (encodedQuery) {
      encodedQuery.disabled = false;
      this.input.removeAttribute('name');
      this.querySelector('form')?.addEventListener('submit', () => {
        encodedQuery.value = this.getScopedQuery();
      });
    }

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  disconnectedCallback() {
    this.invalidatePendingRequest();
  }

  invalidatePendingRequest() {
    this.requestId += 1;
    this.requestController?.abort();
    this.requestController = null;
    this.setLoadingState(false);
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('[data-search-product-types]');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  onKeyDown(event) {
    if (event.key !== 'Escape' || !this.classList.contains(this.states.OPEN)) return;
    event.preventDefault();
    this.input.focus();
    this.toggleSearchState(false);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  updateScopeLabel() {
    const category = this.searchProductTypes?.value || '';
    const template = this.dataset.scopePlaceholder || 'Search [category]…';
    const label = category ? template.replace('[category]', category) : (this.dataset.allPlaceholder || 'Search all products…');
    this.input.placeholder = label;
    this.input.setAttribute('aria-label', label);
    const productOnly = this.querySelector('[data-search-product-only]');
    if (productOnly) productOnly.disabled = !category;
    this.toggleAttribute('data-scoped', Boolean(category));
  }

  handleProductTypeChange(evt) {
    this.invalidatePendingRequest();
    this.updateScopeLabel();
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  getScopedQuery() {
    const category = this.searchProductTypes?.value || '';
    if (!category) return this.getQuery();
    const escapedCategory = category.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    return `product_type:"${escapedCategory}" AND (${this.getQuery() || '*'})`;
  }

  setupURL() {
    const category = this.searchProductTypes?.value || '';
    // Scoped suggestions and full submission share Shopify's native search expression.
    // This also works when Product type is not configured as a storefront facet.
    const path = category ? FoxTheme.routes.search_url : FoxTheme.routes.predictive_search_url;
    const url = new URL(`${window.shopUrl}${path}`);
    url.searchParams.set('q', this.getScopedQuery());
    if (category) {
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      url.searchParams.set('section_id', 'sitc-scoped-search');
    } else {
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    }
    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.invalidatePendingRequest();
    this.removeAttribute('error');
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url, this.requestId);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText);
  }

  renderSectionFromFetch(url, requestId) {
    this.requestController = new AbortController();
    this.setLoadingState(true);

    fetch(url, { signal: this.requestController.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        if (requestId !== this.requestId) return;
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        if (requestId !== this.requestId || error.name === 'AbortError') return;
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        if (requestId !== this.requestId) return;
        this.requestController = null;
        this.setLoadingState(false);
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id) || parsedDoc.querySelector('[data-sitc-scoped-results]');

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
        this.setAttribute('results', 'true');

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    if (!isOpen) this.invalidatePendingRequest();
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
